
import { isBoolean, isFunction, isObject } from "@ciaopeople/cp-shatsu/TypeChecking";

import { NotificationSystemType } from "../api/types/SubscriptionTypes.js";
import { NotificationDeviceType } from "../api/types/SubscriptionTypes.js";
import { DeviceSubscription, FCMReqObj } from "../api/types/SubscriptionTypes.js";
import { AuthProviderIdentifier } from "../login/AuthProviderIdentifier.js";
import { StorableSession } from "../session/StorableSession.js";
import { ThirdPartyCientAuthDataInterface} from "../login/data/LoginData.js";
import { SubscriptionStatus } from "../subscriptions/PushServiceInterface.js";
import { NativeAppSupportedFeatures } from "./NativeAppSupportedFeatures.js";

export interface NotificationDeviceData extends Omit<FCMReqObj, "device_type"> {
    device_os: NotificationDeviceType,
    timestamp: number,
    fid: string,
}

export interface NotificationDeviceInfo {
    status: SubscriptionStatus.ENABLED | SubscriptionStatus.DENIED;
    device?: NotificationDeviceData
}

export function notificationDeviceInfoToDeviceSubscription(ndi: NotificationDeviceInfo): DeviceSubscription {

    const os = ndi && ndi.device && ndi.device.device_os && 
        ndi.device.device_os.charAt(0).toLowerCase() == "a"?NotificationDeviceType.ANDROID:NotificationDeviceType.IOS;

    return {
        notificationSystemType: NotificationSystemType.Firebase,
        token: ndi.device.token,
        deviceId: ndi.device.device_id?ndi.device.device_id:"",
        deviceType: os,
        deviceName: ndi.device.device_name?ndi.device.device_name:"",
        enabled: false
    };
}

export interface ProposeSubscriptionResponse {
    asked: boolean
}

export function isProposeSubscriptionResponse(obj: any): obj is ProposeSubscriptionResponse {
    return isObject(obj) && isBoolean(obj['asked']);
}


export interface NativeAppLoginResponse {
    token: string,
    lastLoginProvider: AuthProviderIdentifier
    isNewUser?: boolean
}

export interface GetStoredSessionResponse extends NativeAppLoginResponse {
    noSession?: boolean
}

export interface CpNativeAppPublicBridge {
    getStoredSession(): Promise<GetStoredSessionResponse>
}


export interface CpNativeAppUpstreamBridge extends CpNativeAppPublicBridge {
    resolveCallback(callbackId: string, data?: any): void,
    requireLogin(provider_suggestion: AuthProviderIdentifier): Promise<NativeAppLoginResponse>,
    requireFacebookLogin(): void,
    requireNotificationEnable(): void,
    shareUrl(title: string, url: string): void,
    sessionChange(session: StorableSession, os: string): void
    getNotificationDeviceInfo(): Promise<NotificationDeviceInfo>
    
    proposeSubscription(): Promise<ProposeSubscriptionResponse>
    logoutExecuted(): void;
    personalDataDeletionCompleted(): void;
    setAnalyticsUserProperties(kmap: {[k: string]: string|number|null}): void;

    
}


export function isCpNativeAppUpstreamBridge(obj: any): obj is CpNativeAppUpstreamBridge {
    return isObject(obj) &&
            isFunction(obj.resolveCallback) &&
            isFunction(obj.requireLogin) &&
            isFunction(obj.requireFacebookLogin) &&
            isFunction(obj.requireNotificationEnable) &&
            isFunction(obj.shareUrl) &&
            isFunction(obj.sessionChange) &&
            isFunction(obj.getNotificationDeviceInfo) &&
            isFunction(obj.setAnalyticsUserProperties);
}


export enum NativeAppSetSessionCause {

    SESSION_REFRESH = 0,
    LOGIN = 1

}


export interface CpNativeAppDownstreamBridge {
    nativeAppSetSession(token: string): void;
    nativeAppSetSessionWithProvider(provider: AuthProviderIdentifier, token: string, cause: NativeAppSetSessionCause, isNewUser: boolean): void;

    /**
     * Lanciato dall'app per segnalare il nuovo stato del flag di optout
     */
    nativeAppSetTrackingOptOut(v: boolean): void;
    /**
     * Traccia l'apertura di una notifica su IOS (*vedi #10765*)
     */
    collectStatNotificationOpen();

    nativeAppSignIn(token: string, tuple: ThirdPartyCientAuthDataInterface);


    
}

export function isCpNativeAppDownstreamBridge(obj: any): obj is CpNativeAppDownstreamBridge {
    return isObject(obj) &&
        isFunction(obj.nativeAppSetSession) &&
        isFunction(obj.nativeAppSetSessionWithProvider) &&
        isFunction(obj.nativeAppSetTrackingOptOut) &&
        isFunction(obj.collectStatNotificationOpen);

}

/* c8 ignore start */
export interface CpNativeAppIntegration extends CpNativeAppDownstreamBridge {

    nativeapp: CpNativeAppUpstreamBridge;
    nativeAppFeatures: NativeAppSupportedFeatures;
    
}

export function isCpNativeAppIntegration(obj: any): obj is CpNativeAppIntegration {
    return isObject(obj) &&
            isCpNativeAppUpstreamBridge(obj.nativeapp) &&
            obj.nativeAppFeatures instanceof NativeAppSupportedFeatures;
}

/* c8 ignore stop */
